import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export enum CarFormControls {
  companyCode = 'companyCode',
}

export interface CarFormValues {
  [CarFormControls.companyCode]: string | null;
}

export interface CarFormState {
  createCarCompanyCode: UseMutationResult<void, Error, CarFormValues, unknown>;
  handleReset: () => void;
  form: UseFormReturn<CarFormValues>;
  isLoading: boolean;
  disabled: boolean;
}

export function useCarForm({ onConnected }: { onConnected?: () => void }): CarFormState {
  const { t } = useTranslation('benefitsModule');
  const { tenant, invalidateTenant, isLoading } = useTenant();
  const form = useForm<CarFormValues>({
    mode: 'onChange',
    defaultValues: { companyCode: tenant?.carCompanyCode ?? null },
  });
  const { enqueueSnackbar } = useSnackbar();
  const withMessage = useWithMessage();

  const createCarCompanyCode = useMutation({
    mutationFn: async (formValue: CarFormValues) => {
      await apis.car.updateCarSettings({
        tenantId: tenant!.id,
        updateCarSettingsDTO: { carCompanyCode: formValue.companyCode ?? '' },
      });
    },
    onSuccess: async () => {
      enqueueSnackbar(t('CAR.connect.form.success'), { variant: 'success' });
      invalidateTenant(onConnected);
    },
    onError: withMessage,
  });

  const handleReset = useCallback(() => {
    if (!tenant) {
      return;
    }

    form.reset({
      companyCode: tenant.carCompanyCode,
    });
  }, [form, tenant]);

  useEffect(handleReset, [handleReset]);

  return useMemo(() => {
    return {
      createCarCompanyCode,
      handleReset,
      form,
      isLoading: isLoading || createCarCompanyCode.isPending,
      disabled: !form.formState.isDirty || !form.formState.isValid,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createCarCompanyCode, form, handleReset, form.formState.isDirty, form.formState.isValid, isLoading]);
}
